import {
  createContext,
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { Icon_Info16 } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import type React from "react";
import { createPortal } from "react-dom";

import style from "./InfoTooltip.module.css";

interface InfoTooltipProps {
  content: React.ReactNode;
  trackTooltipOpened?: () => void;
}

export const OverlayContext = createContext<{
  overlayRef: RefObject<HTMLDivElement | null> | undefined;
}>({ overlayRef: undefined });

export const OverlayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  return (
    <>
      <OverlayContext.Provider value={{ overlayRef }}>{children}</OverlayContext.Provider>
      <div ref={overlayRef} style={{ position: "relative" }}></div>
    </>
  );
};

export const TRACKING_TIMEOUT = 800;
const TOOLTIP_OFFSET_FROM_TRIGGER = 10;

type TooltipState = "closed" | "opening" | "open" | "hidden";

const InfoTooltip = ({ content, trackTooltipOpened }: InfoTooltipProps) => {
  const [tooltipState, setTooltipState] = useState<TooltipState>("closed");
  const { overlayRef } = useContext(OverlayContext);
  const {
    tooltipRef,
    triggerRef,
    markerRef: positioningMarkerRef,
    tooltipStyle,
    markerStyle,
    updatePosition,
  } = useTooltipPositioning(tooltipState !== "closed", content);

  const trackingTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if ((tooltipState === "open" || tooltipState === "opening") && trackTooltipOpened) {
      trackingTimeoutRef.current = window.setTimeout(trackTooltipOpened, TRACKING_TIMEOUT);
      return () => {
        if (trackingTimeoutRef.current !== null) {
          clearTimeout(trackingTimeoutRef.current);
          trackingTimeoutRef.current = null;
        }
      };
    }
  }, [tooltipState, trackTooltipOpened]);

  const handleTransitionEnd = useCallback(() => {
    if (tooltipState === "hidden") {
      setTooltipState("closed");
    }
  }, [tooltipState]);

  const hideTooltip = useCallback(() => {
    if (trackingTimeoutRef.current !== null) {
      clearTimeout(trackingTimeoutRef.current);
      trackingTimeoutRef.current = null;
    }
    setTooltipState("hidden");
  }, []);

  useEffect(() => {
    if (tooltipState !== "open" && tooltipState !== "opening") {
      return;
    }
    document.addEventListener("scroll", hideTooltip, true);

    return () => {
      document.removeEventListener("scroll", hideTooltip, true);
    };
  }, [tooltipState, hideTooltip]);

  const showTooltip = useCallback(() => {
    setTooltipState((previous) => {
      if (previous === "open" || previous === "opening") {
        return previous;
      }
      return "opening";
    });
  }, []);

  useLayoutEffect(() => {
    if (tooltipState !== "opening") {
      return;
    }

    updatePosition();
    const frameId = requestAnimationFrame(() => {
      updatePosition();
      setTooltipState("open");
    });

    return () => cancelAnimationFrame(frameId);
  }, [tooltipState, updatePosition]);

  const tooltipContent = (
    <>
      <div
        ref={tooltipRef}
        className={classNames(style.tooltip, style.show, tooltipState === "open" && style.visible)}
        style={tooltipStyle}
        onTransitionEnd={handleTransitionEnd}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        {content}
      </div>
      <div
        ref={positioningMarkerRef}
        className={classNames(
          style.tooltipMarker,
          style.show,
          tooltipState === "open" && style.visible
        )}
        style={markerStyle}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      ></div>
    </>
  );

  const portalTarget = overlayRef?.current || document.body;

  return (
    <>
      <span
        ref={triggerRef}
        role="tooltip"
        className={style.info}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        <Icon_Info16 />
      </span>
      {tooltipState !== "closed" && createPortal(tooltipContent, portalTarget)}
    </>
  );
};

const MARGIN_PX = 20; // see @media query in CSS

const useTooltipPositioning = (isVisible: boolean, content: ReactNode) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const markerRef = useRef<HTMLDivElement>(null);

  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties>({});
  const [markerStyle, setMarkerStyle] = useState<CSSProperties>({});

  const updatePosition = useCallback(() => {
    const tooltip = tooltipRef.current;
    const trigger = triggerRef.current;
    const marker = markerRef.current;

    if (!tooltip || !trigger) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const screenWidth = window.innerWidth;

    const top = triggerRect.top - tooltipRect.height - TOOLTIP_OFFSET_FROM_TRIGGER;
    let left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

    const correctionFromLeft = Math.max(MARGIN_PX - left, 0);
    const correctionFromRight = Math.max(left + tooltipRect.width + MARGIN_PX - screenWidth, 0);
    left = left + correctionFromLeft - correctionFromRight;

    const newTooltipStyle: CSSProperties = {
      top: `${Math.max(MARGIN_PX, top)}px`,
      left: `${left}px`,
    };
    setTooltipStyle(newTooltipStyle);

    if (marker) {
      const markerRect = marker.getBoundingClientRect();
      const markerLeft = triggerRect.left + triggerRect.width / 2 - markerRect.width / 2;
      const markerTop = Math.max(MARGIN_PX, top) + tooltipRect.height;

      const newMarkerStyle: CSSProperties = {
        top: `${markerTop}px`,
        left: `${markerLeft}px`,
      };
      setMarkerStyle(newMarkerStyle);
    }
  }, []);

  const updatePositionCallback = useCallback(() => {
    requestAnimationFrame(() => updatePosition());
  }, [updatePosition]);

  useEffect(() => {
    if (isVisible) {
      window.addEventListener("resize", updatePositionCallback);
      window.addEventListener("scroll", updatePositionCallback);
    }
    return () => {
      window.removeEventListener("resize", updatePositionCallback);
      window.removeEventListener("scroll", updatePositionCallback);
    };
  }, [updatePosition, isVisible, updatePositionCallback]);

  useLayoutEffect(() => {
    if (content && isVisible) {
      requestAnimationFrame(() => {
        updatePosition();
      });
    }
  }, [content, isVisible, updatePosition]);

  return { tooltipRef, triggerRef, markerRef, tooltipStyle, markerStyle, updatePosition };
};

export default InfoTooltip;
